import { cn } from "@newxsystems/shared";
import type { IEffectfulHeroBlockProps } from "./EffectfulHeroBlock.props";
import { EffectfulHeroAction } from "./EffectfulHeroAction";
import { EffectfulHeroEffectStage } from "./EffectfulHeroEffectStage.client";
import { EffectfulHeroCommonContent } from "./EffectfulHeroCommonContent";
import { EffectfulHeroMedia } from "./EffectfulHeroMedia";
import { EffectfulHeroRichText } from "./EffectfulHeroRichText";
import { EffectfulHeroSection } from "./EffectfulHeroSection";
import { TextEffect } from "../../../../text-effects/TextEffect.client";

/**
 * Style-testing copy of `HeroBlock`. Everything it renders is copied into this
 * folder (`EffectfulHero*`), so changing any of it leaves the shared hero untouched;
 * the only shared imports left are `cn` and the app Image/Link registry.
 * Delete this folder and its two factory entries once a direction is chosen.
 */

const EffectfulHeroBackgroundImage = ({
  alignment,
  animation,
  buttons,
  description,
  eyebrow,
  heading,
  headingEffect,
  image,
}: IEffectfulHeroBlockProps) => {
  const centered = alignment === "center";
  return (
    // The band fills the viewport minus the 5rem header allowance, capped at 60rem. The
    // section's own block spacing (3.5rem mobile, 5.5rem from `lg`) is subtracted here so
    // the visible band — not just the box — stays within that cap.
    <div className="relative isolate flex min-h-[min(calc(100dvh-5rem),53rem)] flex-col overflow-hidden bg-background lg:min-h-[min(calc(100dvh-5rem),60rem)]">
      <div className="absolute inset-0">
        <EffectfulHeroMedia
          className="h-full w-full rounded-none"
          framed={false}
          imageClassName="object-cover"
          media={image.image}
          priority
          sizes="100vw"
        />
      </div>
      <EffectfulHeroEffectStage animation={animation} />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-linear-to-t from-void via-void/62 to-void/10"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-linear-to-r from-void/85 via-void/15 to-transparent"
      />

      {/* `pointer-events-none` lets the mouse reach the effect canvas; links keep theirs. */}
      <div className="daylight-container pointer-events-none relative z-10 flex w-full flex-1 flex-col justify-end gap-9 pt-32 pb-14 md:gap-12 md:pb-20 [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
        <div
          className={cn(
            "daylight-hero-lines flex max-w-[62rem] flex-col gap-5 md:gap-6",
            centered && "mx-auto items-center text-center",
          )}
        >
          {eyebrow ? <p className="signal-label text-signal">{eyebrow}</p> : null}
          {heading ? (
            <h1 className="max-w-[15ch] break-words font-daylight-display text-[clamp(2.75rem,7.4vw,5.5rem)] font-semibold leading-[0.94] tracking-[-0.04em] text-balance text-foreground">
              <TextEffect effect={headingEffect} text={heading} />
            </h1>
          ) : null}
          {description ? (
            <EffectfulHeroRichText className="max-w-[54ch] text-ash" content={description} />
          ) : null}
          {buttons?.length ? (
            <div className={cn("flex flex-wrap gap-3 pt-2", centered && "justify-center")}>
              {buttons.map((button, index) => (
                <EffectfulHeroAction action={button} key={`${button.href}-${index}`} />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const EffectfulHeroSplitImage = ({
  alignment,
  animation,
  buttons,
  description,
  eyebrow,
  heading,
  headingEffect,
  image,
}: IEffectfulHeroBlockProps) => (
  <div className="grid min-w-0 gap-4 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] min-h-[min(calc(100dvh-12rem),53rem)] lg:min-h-[min(calc(100dvh-16rem),49rem)]">
    <EffectfulHeroCommonContent
      alignment={alignment}
      buttons={buttons}
      className="daylight-hero-lines min-w-0 justify-end gap-5 md:py-6"
      description={description}
      eyebrow={eyebrow}
      heading={heading}
      headingAs="h1"
      headingClassName="max-w-[11ch] text-[clamp(2.5rem,5.6vw,4.25rem)] leading-[0.96] tracking-[-0.04em]"
      headingEffect={headingEffect}
      layout="stack"
    />
    {/* The aurora sits on the photograph; the wrapper clips both to the image frame. */}
    <div className="relative isolate h-full min-h-72 w-full min-w-0 md:order-last">
      <div className="h-full w-full">
        <EffectfulHeroMedia
          aspectRatio={image.aspectRatio || "16:9"}
          className="h-full w-full"
          imageClassName="h-full object-cover"
          media={image.image}
          priority
          sizes="(min-width: 768px) 55vw, 100vw"
        />
      </div>
      <EffectfulHeroEffectStage animation={animation} className="overflow-hidden rounded-[14px]" />
    </div>
  </div>
);

export const EffectfulHeroBlock = (props: IEffectfulHeroBlockProps) => {
  if (!props.image) return null;
  const split = props.variant === "split-image";
  return (
    <EffectfulHeroSection
      className={props.parentClass}
      containerWidth={split ? "wide" : "bleed"}
      id={props.anchorId}
      spacing={props.spacing}
    >
      {split ? <EffectfulHeroSplitImage {...props} /> : <EffectfulHeroBackgroundImage {...props} />}
    </EffectfulHeroSection>
  );
};

EffectfulHeroBlock.displayName = "Block::EffectfulHeroBlock";
